/**
 * Mark of one checklist point ("What to check") on one vessel (owner, Q32, D139): Done, N/A (does
 * not apply on this vessel, with an optional short reason) or no mark yet. Both Done and N/A let
 * the question be closed (D126). Stored as `checked` (the point is dealt with: Done or N/A) +
 * `not_applicable` + `reason` (PLAN §2.4).
 */
export const WTC_MARKS = ['none', 'done', 'not_applicable'] as const;
export type WtcMark = (typeof WTC_MARKS)[number];

/** Longest N/A reason, in characters (same limit as the server). */
export const WTC_REASON_MAX = 200;

/** The mark of a stored check row (no row = no mark). */
export function wtcMarkOf(
  row: { checked: boolean; notApplicable?: boolean | null } | null | undefined,
): WtcMark {
  if (row == null || !row.checked) {
    return 'none';
  }
  return row.notApplicable === true ? 'not_applicable' : 'done';
}

/** The stored fields of a mark. */
export function wtcMarkFields(mark: WtcMark): { checked: boolean; notApplicable: boolean } {
  return { checked: mark !== 'none', notApplicable: mark === 'not_applicable' };
}

export interface WtcMarkSummary {
  total: number;
  done: number;
  notApplicable: number;
  /** Done + N/A: points dealt with. */
  marked: number;
}

export function summarizeWtcMarks(marks: readonly WtcMark[]): WtcMarkSummary {
  const done = marks.filter((m) => m === 'done').length;
  const notApplicable = marks.filter((m) => m === 'not_applicable').length;
  return { total: marks.length, done, notApplicable, marked: done + notApplicable };
}
